<template>
  <v-card flat>
    <v-card-title>
      Signup Options
      <v-spacer/>
      <v-btn text @click="reload" :loading="loading">Reload</v-btn>
      <v-btn v-if="!readOnly" color="primary" class="ml-2" @click="save" :loading="saving" :disabled="!dirty">Save</v-btn>
    </v-card-title>
    <v-card-subtitle>
      What the customer app offers on the signup page ("Want to stick to a functional meal plan?",
      "Any dietary restrictions?", "Add a restriction") and on Dietary Preferences, and the allergy
      settings each pick applies to the customer. Both settings on = "we'll adjust it" (the meal may be
      on the personal menu, the kitchen substitutes the ingredient). Both off = "avoid it" (the meal is
      never on the personal menu; if the customer swaps onto one it is delivered as-is with a
      "contains" warning).
      <span v-if="isDefault" class="orange--text text--darken-2">Nothing saved yet — these are the app's built-in lists.</span>
    </v-card-subtitle>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="5000">{{ snackbarText }}</v-snackbar>

    <v-card-text v-if="options">
      <!-- the feature flag: written straight away, not by Save -->
      <div class="d-flex align-center mb-6" data-cy="new-signup-flag">
        <span>
          New signup page: <strong>{{ newSignupEnabled ? 'on for all customers' : 'off' }}</strong>
          <span v-if="!newSignupEnabled" class="text-caption">(only with the ?new-signup=1 link)</span>
        </span>
        <v-btn v-if="!readOnly" small outlined class="ml-4" :loading="savingFlag" @click="toggleNewSignup">
          Turn {{ newSignupEnabled ? 'off' : 'on' }}
        </v-btn>
      </div>

      <!-- 1. functional plans -->
      <h3 class="mb-1">Want to stick to a functional meal plan?</h3>
      <p class="text-caption">The curated menus, in this order. "No — a personalized menu" is always first.</p>
      <v-simple-table dense class="mb-6">
        <thead>
        <tr>
          <th style="width: 90px">order</th>
          <th>stream</th>
          <th>label</th>
          <th style="width: 60px"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(o, i) in options.functional.options" :key="`f-${i}`">
          <td>
            <v-btn icon x-small @click="move(options.functional.options, i, -1)" :disabled="readOnly || i === 0"><v-icon>mdi-arrow-up</v-icon></v-btn>
            <v-btn icon x-small @click="move(options.functional.options, i, 1)" :disabled="readOnly || i === options.functional.options.length - 1"><v-icon>mdi-arrow-down</v-icon></v-btn>
          </td>
          <td><v-select dense hide-details v-model="o.id" :items="streamItems" :disabled="readOnly" @change="dirty = true"/></td>
          <td><v-text-field dense hide-details v-model="o.label" :disabled="readOnly" @input="dirty = true"/></td>
          <td><v-btn icon small @click="remove(options.functional.options, i)" :disabled="readOnly"><v-icon>mdi-delete</v-icon></v-btn></td>
        </tr>
        </tbody>
      </v-simple-table>
      <v-btn outlined small class="mb-8" :disabled="readOnly" @click="add(options.functional.options, {id: '', label: ''})">Add stream</v-btn>

      <!-- 2. diet pills -->
      <h3 class="mb-1">Any dietary restrictions?</h3>
      <p class="text-caption">
        The pills shown on the personal menu. A pill picks a stream (switching the customer's plan), adds a
        restriction with the settings given, or keeps the customer on the personal menu but limits which
        menus it draws from — "menu only from" / "menu leaves out" (Vegan = only from Plant-Based).
        "Exclusive" pills replace each other (one diet at a time).
      </p>
      <v-simple-table dense class="mb-6">
        <thead>
        <tr>
          <th style="width: 90px">order</th>
          <th>label</th>
          <th>stream</th>
          <th>restriction</th>
          <th>menu only from</th>
          <th>menu leaves out</th>
          <th>in personal menu</th>
          <th>substitute ingredient</th>
          <th>exclusive</th>
          <th style="width: 60px"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(o, i) in options.diets.options" :key="`d-${i}`">
          <td>
            <v-btn icon x-small @click="move(options.diets.options, i, -1)" :disabled="readOnly || i === 0"><v-icon>mdi-arrow-up</v-icon></v-btn>
            <v-btn icon x-small @click="move(options.diets.options, i, 1)" :disabled="readOnly || i === options.diets.options.length - 1"><v-icon>mdi-arrow-down</v-icon></v-btn>
          </td>
          <td><v-text-field dense hide-details v-model="o.label" :disabled="readOnly" @input="dirty = true"/></td>
          <td>
            <v-select dense hide-details clearable v-model="o.stream" :items="streamItems" :disabled="readOnly || hasStreams(o)"
                      placeholder="—" @change="onDietStream(o)"/>
          </td>
          <td>
            <v-select dense hide-details v-model="o.restriction" :items="restrictionItems" :disabled="readOnly || !!o.stream || hasStreams(o)"
                      placeholder="—" @change="onDietRestriction(o)"/>
          </td>
          <td>
            <v-select dense hide-details multiple chips small-chips deletable-chips :items="streamItems"
                      :value="streamsOf(o, 'include')" :disabled="readOnly || !!o.stream || !!o.restriction"
                      placeholder="—" @change="setStreams(o, 'include', $event)"/>
          </td>
          <td>
            <v-select dense hide-details multiple chips small-chips deletable-chips :items="streamItems"
                      :value="streamsOf(o, 'exclude')" :disabled="readOnly || !!o.stream || !!o.restriction"
                      placeholder="—" @change="setStreams(o, 'exclude', $event)"/>
          </td>
          <td>
            <v-checkbox dense hide-details class="mt-0" :disabled="readOnly || !!o.stream || hasStreams(o)"
                        :input-value="settingOf(o, 'includeInMenu')" @change="setSetting(o, 'includeInMenu', $event)"/>
          </td>
          <td>
            <v-checkbox dense hide-details class="mt-0" :disabled="readOnly || !!o.stream || hasStreams(o)"
                        :input-value="settingOf(o, 'substituteIngredient')" @change="setSetting(o, 'substituteIngredient', $event)"/>
          </td>
          <td><v-checkbox dense hide-details class="mt-0" v-model="o.exclusive" :disabled="readOnly" @change="dirty = true"/></td>
          <td><v-btn icon small @click="remove(options.diets.options, i)" :disabled="readOnly"><v-icon>mdi-delete</v-icon></v-btn></td>
        </tr>
        </tbody>
      </v-simple-table>
      <v-btn outlined small class="mb-8" :disabled="readOnly"
             @click="add(options.diets.options, {id: '', label: '', restriction: '', settings: {includeInMenu: false, substituteIngredient: false}})">
        Add diet
      </v-btn>

      <!-- 3. the allergy adder -->
      <h3 class="mb-1">Add a restriction (allergies)</h3>
      <p class="text-caption">
        The settings applied to a restriction the customer adds from the list, and which restrictions are
        offered. On a functional plan this list is also what a customer keeps: a diet pill's restriction
        that is not on it is removed when they switch.
      </p>
      <div class="d-flex align-center mb-2">
        <v-checkbox dense hide-details class="mt-0 mr-6" label="in personal menu" :disabled="readOnly"
                    :input-value="allergySettings.includeInMenu !== false"
                    @change="setAllergyDefault('includeInMenu', $event)"/>
        <v-checkbox dense hide-details class="mt-0" label="substitute ingredient" :disabled="readOnly"
                    :input-value="allergySettings.substituteIngredient !== false"
                    @change="setAllergyDefault('substituteIngredient', $event)"/>
      </div>
      <v-select
        v-model="allergyOptionIds"
        :items="restrictionItems"
        label="Restrictions offered"
        multiple chips small-chips deletable-chips clearable dense outlined
        :disabled="readOnly"
        @change="onAllergyOptions"
      />
      <p v-if="allergyFromAvailable" class="text-caption orange--text text--darken-2">
        No list saved — shown is every restriction currently marked "Available On Signup", which is what
        the app offers. Save to keep this list, or edit it first.
      </p>

      <!-- 4. diet presets -->
      <h3 class="mb-1 mt-8">Diet presets</h3>
      <p class="text-caption">
        Which published presets customers can pick, at signup and in their settings. A customer already
        on a hidden preset keeps it: it still shapes their menu and they still see it, until they change
        it. Use this instead of unpublishing, which stops the preset working for those customers.
      </p>
      <p v-if="!presets.length" class="text-caption">No published presets.</p>
      <v-checkbox v-for="p in presets" :key="p.id" dense hide-details class="mt-0" :label="p.title || p.id"
                  :disabled="readOnly" :input-value="presetVisible(p.id)" @change="setPresetVisible(p.id, $event)"/>

      <!-- 5. lifestyle needs -->
      <h3 class="mb-1 mt-8">Any lifestyle needs?</h3>
      <p class="text-caption">
        The presets in this row on the signup page, in this order, instead of under "Presets" in the goals
        row. A customer can pick one preset in all, so a lifestyle need replaces any other preset. The
        row is left out when the list is empty; a preset hidden above does not show here either.
      </p>
      <v-simple-table v-if="options.presets.lifestyle.length" dense class="mb-2">
        <thead>
        <tr>
          <th style="width: 90px">order</th>
          <th>preset</th>
          <th style="width: 60px"></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(id, i) in options.presets.lifestyle" :key="id">
          <td>
            <v-btn icon x-small @click="move(options.presets.lifestyle, i, -1)" :disabled="readOnly || i === 0"><v-icon>mdi-arrow-up</v-icon></v-btn>
            <v-btn icon x-small @click="move(options.presets.lifestyle, i, 1)" :disabled="readOnly || i === options.presets.lifestyle.length - 1"><v-icon>mdi-arrow-down</v-icon></v-btn>
          </td>
          <td>
            {{ presetTitle(id) }}
            <span v-if="!presetVisible(id)" class="orange--text text--darken-2">(hidden)</span>
          </td>
          <td><v-btn icon small @click="remove(options.presets.lifestyle, i)" :disabled="readOnly"><v-icon>mdi-delete</v-icon></v-btn></td>
        </tr>
        </tbody>
      </v-simple-table>
      <v-select ref="lifestyleAdd" dense outlined hide-details style="max-width: 320px" label="Add a preset"
                :items="lifestyleItems" :disabled="readOnly" @change="addLifestyle"/>
    </v-card-text>
  </v-card>
</template>

<script>
import {mapActions} from 'vuex';
import api from '@/api';
import {deepCopy} from '@/store/utils';

export default {
  name: 'SignupOptions',
  data() {
    return {
      options: null,
      saved: null,
      isDefault: false,
      loading: false,
      saving: false,
      dirty: false,
      readOnly: true,
      allergyOptionIds: [],
      // no list saved: allergyOptionIds was filled from the available restrictions
      allergyFromAvailable: false,
      // the published presets, as the app reads them (RTDB meal_presets)
      presets: [],
      newSignupEnabled: false,
      savingFlag: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    streamItems() {
      return Object.values(this.$store.state.streams || {})
        .filter(s => s && s.id)
        .map(s => ({value: s.id, text: `${s.name || s.id} (${s.id})`}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    restrictionItems() {
      // the store keys restrictions by id and by tag id - only the former carry an id
      return Object.values(this.$store.state.restrictions || {})
        .filter(r => r && r.id)
        .map(r => ({value: r.id, text: `${r.text} (${r.id})`}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    // what the app offers while no allergy list is saved
    availableRestrictionIds() {
      return Object.values(this.$store.state.restrictions || {})
        .filter(r => r && r.id && r.available)
        .map(r => r.id);
    },
    allergySettings() {
      return (this.options && this.options.allergies && this.options.allergies.settings) || {};
    },
    // the published presets not yet on the lifestyle list
    lifestyleItems() {
      return this.presets
        .filter(p => !this.options.presets.lifestyle.includes(p.id))
        .map(p => ({value: p.id, text: p.title || p.id}));
    },
  },
  methods: {
    ...mapActions(['fetchRestrictions', 'fetchStreams']),
    notify(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    async reload() {
      this.loading = true;
      try {
        const [{data}, {data: presets}, {data: flag}] = await Promise.all([
          api.get('v2/signup_options'),
          api.get('v2/signup_options/presets'),
          api.get('v2/signup_options/new-signup-flag'),
        ]);
        this.newSignupEnabled = flag.enabled;
        this.presets = presets
          .sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id));
        this.isDefault = !!data.isDefault;
        delete data.isDefault;
        this.options = {
          functional: {options: [], ...(data.functional || {})},
          diets: {options: [], ...(data.diets || {})},
          allergies: {settings: {includeInMenu: true, substituteIngredient: true}, options: [], ...(data.allergies || {})},
          // RTDB drops an empty list
          presets: this.presetLists(data.presets),
        };
        this.options.functional.options = this.options.functional.options || [];
        this.options.diets.options = this.options.diets.options || [];
        this.options.allergies.options = this.options.allergies.options || [];
        this.allergyOptionIds = this.options.allergies.options.map(o => o.id);
        this.allergyFromAvailable = !this.allergyOptionIds.length;
        if (this.allergyFromAvailable) this.allergyOptionIds = [...this.availableRestrictionIds];
        this.saved = deepCopy(this.options);
        // a list filled from the available restrictions is not saved yet
        this.dirty = this.allergyFromAvailable;
      } catch (e) {
        this.notify('Failed to load: ' + e.message, 'error');
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const body = deepCopy(this.options);
        body.allergies.options = this.allergyOptionIds.map(id => ({id}));
        const {data} = await api.put('v2/signup_options', body);
        this.options = data;
        this.allergyOptionIds = (data.allergies.options || []).map(o => o.id);
        this.allergyFromAvailable = false;
        this.saved = deepCopy(data);
        this.isDefault = false;
        this.dirty = false;
        this.notify('Saved');
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.error) || e.message;
        this.notify('Failed to save: ' + message, 'error');
      } finally {
        this.saving = false;
      }
    },
    async toggleNewSignup() {
      const enabled = !this.newSignupEnabled;
      const what = enabled
        ? 'Turn the new signup page ON for all customers? This also shows the diet pills on Dietary Preferences.'
        : 'Turn the new signup page OFF for all customers?';
      if (!window.confirm(`${what} It takes effect immediately.`)) return;
      this.savingFlag = true;
      try {
        const {data} = await api.put('v2/signup_options/new-signup-flag', {enabled});
        this.newSignupEnabled = data.enabled;
        this.notify(`New signup page ${data.enabled ? 'on' : 'off'}`);
      } catch (e) {
        const message = (e.response && e.response.data && e.response.data.error) || e.message;
        this.notify('Failed to change the flag: ' + message, 'error');
      } finally {
        this.savingFlag = false;
      }
    },
    add(list, item) {
      list.push(item);
      this.dirty = true;
    },
    remove(list, i) {
      list.splice(i, 1);
      this.dirty = true;
    },
    move(list, i, by) {
      const j = i + by;
      if (j < 0 || j >= list.length) return;
      const [item] = list.splice(i, 1);
      list.splice(j, 0, item);
      this.dirty = true;
    },
    settingOf(option, key) {
      return !(option.settings && option.settings[key] === false);
    },
    setSetting(option, key, value) {
      this.$set(option, 'settings', {...(option.settings || {}), [key]: !!value});
      this.dirty = true;
    },
    // a diet pill is a stream, a restriction OR a streams rule (menu only from /
    // leaves out — users/<uid>.stream_preferences); its id follows what it names
    hasStreams(option) {
      return !!option.streams && ['include', 'exclude'].some(k => (option.streams[k] || []).length > 0);
    },
    streamsOf(option, list) {
      return (option.streams && option.streams[list]) || [];
    },
    setStreams(option, list, ids) {
      const other = list === 'include' ? 'exclude' : 'include';
      const streams = {...(option.streams || {}), [list]: ids || []};
      // a stream is in one list only
      streams[other] = (streams[other] || []).filter(id => !(ids || []).includes(id));
      ['include', 'exclude'].forEach(k => { if (!streams[k].length) delete streams[k]; });
      if (Object.keys(streams).length) {
        this.$set(option, 'streams', streams);
        this.$delete(option, 'restriction');
        this.$delete(option, 'settings');
        this.$delete(option, 'stream');
        if (!option.id) option.id = (option.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      } else {
        this.$delete(option, 'streams');
      }
      this.dirty = true;
    },
    onDietStream(option) {
      if (option.stream) {
        this.$delete(option, 'restriction');
        this.$delete(option, 'settings');
        this.$delete(option, 'streams');
        option.id = option.stream;
      }
      this.dirty = true;
    },
    onDietRestriction(option) {
      if (option.restriction) {
        this.$delete(option, 'streams');
        option.id = option.restriction;
        if (!option.settings) this.$set(option, 'settings', {includeInMenu: false, substituteIngredient: false});
      }
      this.dirty = true;
    },
    setAllergyDefault(key, value) {
      this.$set(this.options.allergies, 'settings', {...this.allergySettings, [key]: !!value});
      this.dirty = true;
    },
    onAllergyOptions() {
      this.dirty = true;
    },
    presetLists(presets) {
      const {hidden, lifestyle} = presets || {};
      return {hidden: hidden || [], lifestyle: lifestyle || []};
    },
    presetTitle(id) {
      const preset = this.presets.find(p => p.id === id);
      return (preset && preset.title) || id;
    },
    addLifestyle(id) {
      if (!id) return;
      this.add(this.options.presets.lifestyle, id);
      // the select is an action, not a value: back to the label for the next one
      this.$nextTick(() => this.$refs.lifestyleAdd.reset());
    },
    presetVisible(id) {
      return !this.options.presets.hidden.includes(id);
    },
    setPresetVisible(id, visible) {
      const others = this.options.presets.hidden.filter(hiddenId => hiddenId !== id);
      this.options.presets.hidden = visible ? others : [...others, id];
      this.dirty = true;
    },
  },
  async mounted() {
    this.readOnly = !!this.$route.meta.readOnly;
    await Promise.all([this.fetchRestrictions(), this.fetchStreams()]);
    return this.reload();
  },
};
</script>
